/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { useAppConfig } from '@airasia-rn/ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isChinaGeoId } from '../utils/signupLoginUtils';
import { useAccountConfig } from '../config/AccountConfigProvider';
import type { LoginNotification } from '../widgets/LoginView/types';

/**
 * China market: signup and login wait for the T&C checkbox. The host can
 * decide when it shows (AccountConfig.showChinaTerms); otherwise geoId CN.
 */
export function useChinaTermsGate(
  setNotification: (notification: LoginNotification | null) => void,
) {
  const { t } = useTranslation('account');
  const { config } = useAppConfig();
  const { config: accountConfig } = useAccountConfig();
  const showChinaTerms =
    accountConfig.showChinaTerms ?? isChinaGeoId(config?.geoId);
  const [hasAcceptedChinaTerms, setHasAcceptedChinaTerms] = useState(false);

  /** False (and shows the "agree to continue" error) while the shown terms are unticked. */
  const ensureChinaTermsAccepted = useCallback(() => {
    if (!showChinaTerms || hasAcceptedChinaTerms) return true;
    setNotification({
      message: t('signUpView.chinaTermsRequired'),
      type: NotificationViewStyle.ERROR,
    });
    return false;
  }, [hasAcceptedChinaTerms, setNotification, showChinaTerms, t]);

  const toggleChinaTerms = useCallback(() => {
    setHasAcceptedChinaTerms((prev) => !prev);
    setNotification(null);
  }, [setNotification]);

  return {
    showChinaTerms,
    hasAcceptedChinaTerms,
    toggleChinaTerms,
    ensureChinaTermsAccepted,
  };
}
